import { useMyUser } from '@consigli/hooks';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/layouts/app-shell/app-shell';
import { formatDisplayName } from '@/util/format-name';

import { DemoModeSwitch } from './demo-mode-switch';
import { ProfileBanner } from './profile-banner';
import { type ProfileDetail, ProfileCard, ProfileDetailList } from './profile-card';
import { ProfileInvites } from './profile-invites';

/** The frame's 560 column, as the Settings tab uses. */
const COLUMN = 'w-[560px] max-w-full';

/** What a field reads when the account carries nothing for it. */
const BLANK = '—';

/**
 * The redesigned My Profile screen. Figma 11877-188160 (superuser) and
 * 11877-187948 (user).
 *
 * The two frames are the same page with one word changed, so this is one
 * component and the role picks the copy. Both frames carry the Permissions card
 * with the superuser sentence in it — on the user frame that is a leftover from
 * duplicating the superuser one, so the card renders for a superuser only.
 *
 * Neither frame draws the Project access card the earlier design had, and it is
 * gone on purpose rather than missed.
 */
export const UserProfileRedesignPage: FC = () => {
  const { t } = useTranslation();
  const { user } = useMyUser();

  // The same three-way the page this replaces used, with the superuser arm's
  // hardcoded "SuperUser 🦸‍♂️" replaced by a translated string. The frame says
  // "Superuser".
  const role = user.isSuperuser
    ? t('profile.superuser-role')
    : user.isOrgAdmin
      ? t('profile.admin')
      : t('profile.user');

  /**
   * The frame's Permissions card, which says something different for each role.
   *
   * "Yes" is the truth rather than the aspiration: `ProjectViewSet` declares no
   * `permission_classes`, so DRF falls back to `IsAuthenticated`, and the
   * `clientdata.add_project` permission the groups are granted is never checked
   * anywhere in the API. Any signed-in account can create a project today.
   *
   * The admin lines describe the role rather than reading anything off the
   * account, which is what the frame draws — an admin's answer does not vary.
   */
  const permissions: ProfileDetail[] = user.isOrgAdmin
    ? [
        {
          label: t('profile.can-create-projects'),
          value: t('profile.admin-can-create-projects'),
        },
        { label: t('profile.project-access'), value: t('profile.admin-project-access') },
      ]
    : [{ label: t('profile.can-create-projects'), value: t('profile.yes') }];

  const details: ProfileDetail[] = [
    { label: t('profile.email'), value: user.email },
    { label: t('profile.role'), value: role },
    { label: t('profile.organization'), value: user.organizationName || BLANK },
    { label: t('profile.country'), value: user.ssoCountry || BLANK },
    { label: t('profile.business-line'), value: user.ssoBusinessLine || BLANK },
  ];

  return (
    <AppShell breadcrumbs={[{ label: t('navbar.breadcrumbs.myprofile') }]}>
      {/* py-8 and the centred column match the Settings tab, which is the other
          560 page in the redesign. */}
      <div className="flex flex-col items-center py-8">
        <div className={`${COLUMN} flex flex-col gap-4`}>
          {/* The frame's `heading 4` — 20/24 at weight 500. */}
          <h1 className="text-xl leading-6 font-medium text-[var(--ds-color-text-primary)]">
            {t('navbar.breadcrumbs.myprofile')}
          </h1>

          <ProfileBanner
            crowned={user.isSuperuser}
            name={formatDisplayName(user.firstName, user.lastName)}
            role={role}
          />

          <ProfileCard title={t('profile.account-info')}>
            <ProfileDetailList details={details} />
          </ProfileCard>

          {/* Above Permissions, because an invitation is something to act on
              and the cards below only report. A superuser is never invited
              anywhere — they already reach everything — which is the same gate
              the page this replaces put on it. */}
          {!user.isSuperuser && <ProfileInvites />}

          <ProfileCard title={t('profile.permissions')}>
            {user.isSuperuser ? (
              <>
                {/* One sentence and no rows: a superuser's answer to every row
                    would be the same, so the frame stops asking. Medium weight,
                    unlike the values elsewhere — the frame sets this line as
                    `paragraph small/medium`. */}
                <p className="text-sm leading-5 font-medium text-[var(--ds-color-text-primary)]">
                  {t('profile.superuser')}
                </p>

                <DemoModeSwitch />
              </>
            ) : (
              <ProfileDetailList details={permissions} />
            )}
          </ProfileCard>
        </div>
      </div>
    </AppShell>
  );
};
